import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import AutoStoriesRoundedIcon from "@mui/icons-material/AutoStoriesRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import { CurrencyContext } from "../Contexts/CurrencyContext";
import { getProgramsCourseOverview } from "../Components/enquiryProfileApi";
import s from "./CourseOverviewPage.module.scss";

function courseShowsDiscountedPricing(c) {
  const pct = Number(c?.discount_percentage);
  if (Number.isFinite(pct) && pct > 0) return true;
  const orig = Number(c?.original_price ?? c?.fees);
  const sale = Number(c?.discounted_price ?? c?.fees);
  if (!Number.isFinite(orig) || !Number.isFinite(sale)) return false;
  return orig > sale + 0.005;
}

function formatDuration(durationValue) {
  const totalMinutes = Number(durationValue);
  if (!Number.isFinite(totalMinutes)) return durationValue || "—";
  const safeMinutes = Math.max(0, Math.round(totalMinutes));
  const hours = Math.floor(safeMinutes / 60);
  const minutes = safeMinutes % 60;
  if (hours === 0) return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  if (minutes === 0) return `${hours} hour${hours !== 1 ? "s" : ""}`;
  return `${hours}h ${minutes}m`;
}

function coursePriceForSort(c) {
  const n = courseShowsDiscountedPricing(c)
    ? Number(c.discounted_price ?? c.fees)
    : Number(c.fees);
  return Number.isFinite(n) ? n : -Infinity;
}

function computeDiscountLabel(c) {
  const p = Number(c.discount_percentage);
  if (Number.isFinite(p) && p > 0) return `${p}% OFF`;
  const o = Number(c.original_price ?? c.fees);
  const d = Number(c.discounted_price ?? c.fees);
  if (Number.isFinite(o) && o > 0 && Number.isFinite(d)) {
    const inferred = Math.round((1 - d / o) * 100);
    if (inferred > 0) return `${inferred}% OFF`;
  }
  return "Sale";
}

export default function CourseOverviewPage() {
  const { formatCurrency } = useContext(CurrencyContext);
  const [programsWithCourses, setProgramsWithCourses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = search.trim();
      setDebouncedSearch((prev) => {
        if (next !== prev) setPage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProgramsCourseOverview({
        search: debouncedSearch || undefined,
        page,
        page_size: pageSize,
      });
      const programs = Array.isArray(data?.programs) ? data.programs : [];
      setProgramsWithCourses(
        programs.map((program) => ({
          ...program,
          courses: Array.isArray(program.courses) ? program.courses : [],
        }))
      );
      const t = Number(data?.total);
      setTotal(Number.isFinite(t) ? t : programs.length);
    } catch {
      setProgramsWithCourses([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const totalCourses = useMemo(
    () => programsWithCourses.reduce((sum, p) => sum + p.courses.length, 0),
    [programsWithCourses]
  );

  const activePromos = useMemo(
    () =>
      programsWithCourses.reduce(
        (sum, p) => sum + p.courses.filter(courseShowsDiscountedPricing).length,
        0
      ),
    [programsWithCourses]
  );

  return (
    <Box className={s.root}>
      <Box className={s.mainContent}>
        {/* ── hero ── */}
        <header className={s.heroHeader}>
          <Typography className={s.heroTitle}>Course Overview</Typography>
          <Typography className={s.heroSubtitle}>
            Browse all programs and courses at a glance — pricing, duration, and
            active promotions in one place.
          </Typography>

          <div className={s.statsRow}>
            <div className={s.statCard}>
              <span className={s.statIconWrap}>
                <SchoolRoundedIcon className={s.statIcon} />
              </span>
              <div>
                <Typography className={s.statValue}>
                  {loading ? "—" : total}
                </Typography>
                <Typography className={s.statLabel}>Programs</Typography>
              </div>
            </div>
            <div className={s.statCard}>
              <span className={s.statIconWrap}>
                <AutoStoriesRoundedIcon className={s.statIcon} />
              </span>
              <div>
                <Typography className={s.statValue}>
                  {loading ? "—" : totalCourses}
                </Typography>
                <Typography className={s.statLabel}>Courses</Typography>
              </div>
            </div>
            {activePromos > 0 && (
              <div className={s.statCard}>
                <span className={s.statIconWrap}>
                  <LocalOfferRoundedIcon className={s.statIcon} />
                </span>
                <div>
                  <Typography className={s.statValue}>{activePromos}</Typography>
                  <Typography className={s.statLabel}>Promotions</Typography>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* ── search ── */}
        <div className={s.searchBar}>
          <input
            className={s.searchInput}
            type="text"
            placeholder="Search programs, courses, or types"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <span className={s.searchIconWrap}>
            <SearchRoundedIcon style={{ fontSize: 20 }} />
          </span>
        </div>

        {/* ── content ── */}
        {loading ? (
          <div className={s.loadingWrap}>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={180}
                className={s.skeletonCard}
                animation="wave"
              />
            ))}
          </div>
        ) : programsWithCourses.length === 0 ? (
          <div className={s.emptyState}>
            <SearchOffRoundedIcon className={s.emptyIcon} />
            <Typography className={s.emptyTitle}>
              No programs found
            </Typography>
            <Typography className={s.emptyText}>
              {search
                ? `Nothing matches "${search}". Try a broader search term.`
                : "There are no programs or courses to display yet."}
            </Typography>
          </div>
        ) : (
          <>
            <div className={s.programList}>
              {programsWithCourses.map((program, idx) => (
                <Card
                  key={program.id}
                  className={s.programCard}
                  variant="outlined"
                  style={{ "--card-i": idx }}
                >
                  {/* program header */}
                  <div className={s.programHeader}>
                    {program.logo_url ? (
                      <Avatar
                        src={program.logo_url}
                        alt={program.name}
                        className={s.programAvatar}
                        variant="rounded"
                      />
                    ) : (
                      <Avatar className={s.programAvatarFallback} variant="rounded">
                        {program.name?.charAt(0) || "P"}
                      </Avatar>
                    )}
                    <div className={s.programInfo}>
                      <Typography className={s.programName}>
                        {program.name}
                      </Typography>
                      {(program.description || program.website) && (
                        <Typography className={s.programDesc}>
                          {program.description || program.website}
                        </Typography>
                      )}
                    </div>
                    <span className={s.courseCountBadge}>
                      {program.courses.length}{" "}
                      {program.courses.length === 1 ? "course" : "courses"}
                    </span>
                  </div>

                  {/* course table */}
                  {program.courses.length === 0 ? (
                    <div className={s.noCourses}>
                      No courses in this program yet.
                    </div>
                  ) : (
                    <div className={s.tableWrap}>
                      <TableContainer
                        component={Paper}
                        className={s.courseTable}
                        variant="outlined"
                      >
                        <Table size="small" className={s.courseTableInner}>
                          <TableHead>
                            <TableRow>
                              <TableCell className={s.tableHeadCell}>
                                Course name
                              </TableCell>
                              <TableCell className={s.tableHeadCell}>
                                Type
                              </TableCell>
                              <TableCell className={s.tableHeadCell}>
                                Duration
                              </TableCell>
                              <TableCell className={s.tableHeadCell} align="right">
                                Fees
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...program.courses]
                              .sort(
                                (a, b) =>
                                  coursePriceForSort(b) - coursePriceForSort(a)
                              )
                              .map((c) => (
                                <TableRow key={c.id} className={s.tableBodyRow}>
                                  <TableCell className={s.tableBodyCell}>
                                    <span className={s.courseName}>{c.name}</span>
                                  </TableCell>
                                  <TableCell className={s.tableBodyCell}>
                                    {c.type ? (
                                      <span className={s.courseType}>{c.type}</span>
                                    ) : (
                                      "—"
                                    )}
                                  </TableCell>
                                  <TableCell className={s.tableBodyCell}>
                                    <span className={s.durationText}>
                                      {formatDuration(c.duration)}
                                    </span>
                                  </TableCell>
                                  <TableCell className={s.tableBodyCell} align="right">
                                    {courseShowsDiscountedPricing(c) ? (
                                      <span className={s.priceCell}>
                                        <Typography className={s.priceOriginal}>
                                          {formatCurrency(
                                            c.original_price ?? c.fees
                                          )}
                                        </Typography>
                                        <Typography className={s.priceSale}>
                                          {formatCurrency(
                                            c.discounted_price ?? c.fees
                                          )}
                                        </Typography>
                                        <Chip
                                          size="small"
                                          label={computeDiscountLabel(c)}
                                          className={s.discountBadge}
                                        />
                                      </span>
                                    ) : (
                                      <span className={s.priceNormal}>
                                        {c.fees != null && c.fees !== ""
                                          ? formatCurrency(c.fees)
                                          : "—"}
                                      </span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  )}
                </Card>
              ))}
            </div>

            {/* pagination */}
            <div className={s.paginationWrap}>
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 25, 50, 100]}
                count={total}
                rowsPerPage={pageSize}
                page={Math.max(0, page - 1)}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                labelRowsPerPage="Programs per page"
              />
            </div>
          </>
        )}
      </Box>
    </Box>
  );
}
